import { useState, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { useParams, useNavigate } from "react-router-dom"
import { useAuth } from "../hooks/useAuth"
import {
    getInvitation,
    acceptInvitation,
    rejectInvitation,
} from "../services/chaperonInvitationsConnector"
import { consoleLog } from "../utilities/helpers"
import chaperonLogo from "../images/brand/chaperon-logo.svg"
import "../css/user-registration.css"

export default function Join() {
    const { token } = useParams()
    const navigate = useNavigate()
    const isLoggedIn = useAuth()

    const [invitation, setInvitation] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isActioning, setIsActioning] = useState(false)

    const redirectToLogin = () => {
        navigate("/user/login", {
            state: {
                invitationToken: token,
                communityName: invitation?.community_name,
                from: { pathname: `/join/${token}` },
            },
        })
    }

    useEffect(() => {
        const fetchInvitation = async () => {
            try {
                setIsLoading(true)
                const data = await getInvitation(token)
                consoleLog("Invitation data:", data)
                // API returns invitation details in data.data or data.invitation
                setInvitation(data.data || data.invitation || data)
            } catch (err) {
                consoleLog("Failed to load invitation:", err)
                setError({
                    status: err.status,
                    message: err.message,
                    invitationStatus: err.response?.invitation_status || null,
                })
            } finally {
                setIsLoading(false)
            }
        }

        if (token) {
            fetchInvitation()
        }
    }, [token])

    const handleAccept = async () => {
        const storedUser = JSON.parse(
            localStorage.getItem("chaperon_user_data"),
        )
        if (!storedUser?.id) return

        setIsActioning(true)
        try {
            await acceptInvitation({
                invitation_token: token,
                user_id: storedUser.id,
            })

            // Clear cached communities to ensure fresh data loads on dashboard
            localStorage.removeItem("chaperon_user_communities")

            // Dispatch event to trigger dashboard refresh
            window.dispatchEvent(new Event("communitiesUpdated"))

            navigate("/community", {
                state: {
                    message: `You've joined ${invitation?.community_name || "the community"} successfully!`,
                },
            })
        } catch (err) {
            consoleLog("Failed to accept invitation:", err)
            if (err.status === 401) {
                redirectToLogin()
                return
            }
            setError({
                message:
                    err.message ||
                    "Failed to accept the invitation. Please try again.",
            })
        } finally {
            setIsActioning(false)
        }
    }

    const handleDecline = async () => {
        const storedUser = JSON.parse(
            localStorage.getItem("chaperon_user_data"),
        )
        if (!storedUser?.id) return

        setIsActioning(true)
        try {
            await rejectInvitation({
                invitation_token: token,
                user_id: storedUser.id,
            })
            navigate("/", {
                state: { message: "Invitation declined." },
            })
        } catch (err) {
            consoleLog("Failed to reject invitation:", err)
            if (err.status === 401) {
                redirectToLogin()
                return
            }
            setError({
                message:
                    err.message ||
                    "Failed to decline the invitation. Please try again.",
            })
        } finally {
            setIsActioning(false)
        }
    }

    const handleRegister = () => {
        navigate("/user/register", {
            state: {
                invitationToken: token,
                communityName: invitation?.community_name,
            },
        })
    }

    const handleLogin = () => {
        navigate("/user/login", {
            state: {
                invitationToken: token,
                communityName: invitation?.community_name,
                from: { pathname: `/join/${token}` },
            },
        })
    }

    // --- Loading ---
    if (isLoading) {
        return (
            <div
                className="auth-container"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Helmet>
                    <title>Loading Invitation - Chaperon</title>
                </Helmet>
                <div className="text-center">
                    <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                    >
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Loading your invitation...</p>
                </div>
            </div>
        )
    }

    // --- Error: already actioned ---
    if (error?.invitationStatus && error.invitationStatus !== "pending") {
        const statusLabel =
            error.invitationStatus === "accepted" ? "accepted" : "declined"
        return (
            <div
                className="auth-container"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Helmet>
                    <title>{`Invitation ${statusLabel === "accepted" ? "Accepted" : "Declined"} - Chaperon`}</title>
                </Helmet>
                <div
                    className="auth-success-card text-center"
                    style={{ maxWidth: 480 }}
                >
                    <div className="mb-4">
                        <i
                            className={`bi ${statusLabel === "accepted" ? "bi-check-circle-fill text-success" : "bi-x-circle-fill text-secondary"}`}
                            style={{ fontSize: "3.5rem" }}
                        ></i>
                    </div>
                    <h2 className="mb-2">
                        Invitation Already{" "}
                        {statusLabel === "accepted" ? "Accepted" : "Declined"}
                    </h2>
                    <p className="text-muted mb-4">
                        This invitation has already been {statusLabel}.{" "}
                        {statusLabel === "accepted" &&
                            "Head to your dashboard to see your community."}
                    </p>
                    {statusLabel === "accepted" && (
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate("/community")}
                        >
                            Go to Dashboard
                        </button>
                    )}
                    {statusLabel !== "accepted" && (
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => navigate("/")}
                        >
                            Go Home
                        </button>
                    )}
                </div>
            </div>
        )
    }

    // --- Error: not found or generic ---
    if (error || !invitation) {
        return (
            <div
                className="auth-container"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Helmet>
                    <title>Invalid Invitation - Chaperon</title>
                </Helmet>
                <div
                    className="auth-success-card text-center"
                    style={{ maxWidth: 480 }}
                >
                    <div className="mb-4">
                        <i
                            className="bi bi-exclamation-circle-fill text-danger"
                            style={{ fontSize: "3.5rem" }}
                        ></i>
                    </div>
                    <h2 className="mb-2">Invitation Not Found</h2>
                    <p className="text-muted mb-4">
                        This invitation link is invalid or has expired. Please
                        ask the sender for a new invitation.
                    </p>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate("/")}
                    >
                        Go Home
                    </button>
                </div>
            </div>
        )
    }

    // --- Main invitation view ---
    const {
        community_name,
        inviter,
        role = "member",
        message: inviteMessage,
    } = invitation

    const roleLabel = role.charAt(0).toUpperCase() + role.slice(1)

    return (
        <div className="auth-container">
            <Helmet>
                <title>{`You've Been Invited to ${community_name} - Chaperon`}</title>
                <meta
                    name="description"
                    content={`${inviter.username} has invited you to join ${community_name} on Chaperon.`}
                />
            </Helmet>

            {/* Left side - brand panel */}
            <div
                className="auth-image-section"
                style={{ backgroundImage: "url(/image/zen-lady-mountain.png)" }}
            >
                <div className="auth-brand-content">
                    <h1>Stay Safe Together</h1>
                    <p>
                        Join a Chaperon community to share safety insights and
                        travel alerts with the people who matter.
                    </p>
                </div>
            </div>

            {/* Right side - invitation card */}
            <div className="auth-form-section">
                <div className="auth-form-container">
                    <div className="auth-logo mb-4 mx-auto text-center">
                        <img
                            src={chaperonLogo}
                            alt="Chaperon"
                            style={{ height: 40 }}
                            onError={(e) => {
                                e.target.style.display = "none"
                            }}
                        />
                    </div>

                    <div className="text-center mb-4">
                        <div
                            className="rounded-circle bg-primary d-inline-flex align-items-center justify-content-center mb-3"
                            style={{ width: 64, height: 64 }}
                        >
                            <i className="bi bi-people-fill text-white fs-4"></i>
                        </div>
                        <h2 className="fw-bold mb-1">You&apos;re Invited!</h2>
                        <p className="text-muted">
                            <strong>{inviter.username}</strong> has invited you
                            to join their community on Chaperon
                        </p>
                    </div>

                    {/* Community details card */}
                    <div
                        className="rounded-3 p-4 mb-4"
                        style={{
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        <div className="d-flex align-items-center mb-3">
                            <i className="bi bi-shield-check text-primary me-2 fs-5"></i>
                            <div>
                                <div
                                    className="fw-semibold"
                                    style={{ color: "#111827" }}
                                >
                                    {community_name}
                                </div>
                                <div
                                    className="small text-muted"
                                    style={{ textTransform: "capitalize" }}
                                >
                                    Role: {roleLabel}
                                </div>
                            </div>
                        </div>

                        {inviteMessage && (
                            <div
                                className="rounded-2 p-3 fst-italic"
                                style={{
                                    background: "#fff",
                                    border: "1px solid #e2e8f0",
                                    color: "#374151",
                                    fontSize: "0.9rem",
                                }}
                            >
                                <i className="bi bi-chat-quote me-2 text-muted"></i>
                                {inviteMessage}
                            </div>
                        )}
                    </div>

                    {/* Action buttons - logged in */}
                    {isLoggedIn ? (
                        <div className="d-grid gap-2">
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={handleAccept}
                                disabled={isActioning}
                            >
                                {isActioning ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Joining...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle me-2"></i>
                                        Accept Invitation
                                    </>
                                )}
                            </button>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleDecline}
                                disabled={isActioning}
                            >
                                Decline
                            </button>
                        </div>
                    ) : (
                        /* Action buttons - not logged in */
                        <div className="d-grid gap-2">
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={handleRegister}
                            >
                                <i className="bi bi-person-plus me-2"></i>
                                Create Account to Accept
                            </button>
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleLogin}
                            >
                                <i className="bi bi-box-arrow-in-right me-2"></i>
                                Log In to Accept
                            </button>
                        </div>
                    )}

                    {/* Inline error (post-load) */}
                    {error && !isLoading && (
                        <div
                            className="alert alert-danger mt-3 mb-0"
                            role="alert"
                        >
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error.message}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
